import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

/* --- helpers ----------------------------------------------- */
const PAGE_SIZES = [10, 25, 50, 100];
const calcEdad = fn => {
  if (!fn) return '—';
  const y = Math.floor((Date.now() - new Date(fn)) / 3.156e10);
  return y + ' años';
};
const fmtFecha = fn => fn ? new Date(fn).toLocaleDateString('es-EC') : '—';
const iniciales = n => n ? n.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase() : '';

/* --- CSS -------------------------------------------------- */
const css = `
.pac-header{background:#fff;color:var(--navy);padding:14px 18px;display:flex;align-items:center;justify-content:space-between;border-radius:10px 10px 0 0;border-bottom:1px solid var(--border);}
.pac-header-title{font-size:13px;font-weight:700;letter-spacing:.5px;text-transform:uppercase;color:var(--navy);}
.pac-toolbar{padding:14px 18px;background:#fff;border-bottom:1px solid var(--border);display:flex;align-items:center;gap:10px;flex-wrap:wrap;}
.pac-table thead tr{background:var(--blue-lt);color:var(--navy);}
.pac-table thead th{padding:13px 16px;font-size:10.5px;font-weight:700;letter-spacing:.5px;text-transform:uppercase;color:var(--navy);border:none;border-bottom:2px solid var(--border);white-space:nowrap;cursor:pointer;user-select:none;}
.pac-table thead th:hover{background:#e2e9f3;}
.pac-table thead th.sort-asc::after{content:' ↑';}
.pac-table thead th.sort-desc::after{content:' ↓';}
.pac-table tbody tr:hover{background:var(--blue-lt);}
.pac-table tbody td{padding:12px 16px;font-size:12.5px;border-bottom:1px solid var(--border);color:var(--text2);}
.pac-footer{padding:14px 18px;background:#fff;border-top:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:8px;border-radius:0 0 10px 10px;}
.pac-footer-info{font-size:11px;color:var(--text3);}
.pag{display:flex;align-items:center;gap:3px;}
.pag-btn{width:28px;height:28px;border-radius:5px;border:1px solid var(--border);background:#fff;font-size:11px;font-weight:600;color:var(--text2);cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all .12s;}
.pag-btn:hover:not(:disabled){background:var(--bg);}
.pag-btn.active{background:var(--navy);color:#fff;border-color:var(--navy);}
.pag-btn:disabled{opacity:.35;cursor:default;}
.pag-ellipsis{font-size:11px;color:var(--text3);padding:0 2px;}
.act-icon{width:28px;height:28px;border-radius:5px;border:1px solid var(--border);background:#fff;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;font-size:14px;transition:all .12s;color:var(--navy);}
.act-icon:hover{background:var(--blue-lt);}
.reg-tabs{display:flex;gap:8px;margin-bottom:0;border-bottom:2px solid var(--border);padding:0 20px;overflow-x:auto;}
.reg-tab{padding:10px 18px;font-size:12px;font-weight:600;color:var(--text3);cursor:pointer;border-bottom:2px solid transparent;margin-bottom:-2px;display:flex;align-items:center;gap:7px;transition:all .15s;white-space:nowrap;}
.reg-tab.active{color:var(--navy);border-bottom-color:var(--navy);}
.reg-tab:hover:not(.active){color:var(--text);}
.section-label{grid-column:1/-1;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.6px;color:var(--text3);padding:6px 0 4px;border-bottom:1px solid var(--bg);margin-top:4px;}
.pac-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:16px;}
.pac-layout{display:flex;gap:16px;align-items:flex-start;}
.pac-side-panel{width:300px;flex-shrink:0;background:var(--surface);border-radius:10px;border:1px solid var(--border);overflow:hidden;}
@media (max-width: 900px){
  .pac-kpis{grid-template-columns:repeat(2,1fr);}
  .pac-layout{flex-direction:column;}
  .pac-side-panel{width:100%;}
}
@media (max-width: 520px){
  .pac-kpis{grid-template-columns:1fr;}
}

.table-wrap{overflow-x:auto;}

/* ── Tarjetas de paciente (celular) — ocultas por defecto ── */
.pac-cards-mobile{display:none;}
.pat-card{background:var(--surface);border:1px solid var(--border);border-radius:11px;padding:12px 13px;margin-bottom:9px;}
.pat-top{display:flex;gap:10px;align-items:flex-start;}
.pat-av{width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;flex-shrink:0;}
.pat-info{flex:1;min-width:0;}
.pat-name{font-size:12.5px;font-weight:600;color:var(--text);line-height:1.35;}
.pat-meta{display:flex;flex-wrap:wrap;gap:3px 8px;margin-top:4px;font-size:10.5px;color:var(--text3);}
.pat-acts{display:flex;gap:7px;margin-top:10px;justify-content:flex-end;border-top:1px solid var(--bg);padding-top:9px;}
.pat-acts button{width:32px;height:32px;border-radius:8px;border:1px solid var(--border);background:#fff;font-size:14px;color:var(--navy);cursor:pointer;display:flex;align-items:center;justify-content:center;}
.pat-acts button.on{background:var(--navy);color:#fff;border-color:var(--navy);}
.pat-acts button:hover{background:var(--blue-lt);}

@media (max-width: 680px){
  .pac-table-desktop{display:none;}
  .pac-cards-mobile{display:block;}
  .pac-header{flex-wrap:wrap;gap:8px;}
  .pac-toolbar{gap:8px;}
}
`;

/* ===========================================
   LISTA DE PACIENTES
=========================================== */
function ListaPacientes({ onNuevo, onEditar, onHistoria, onImprimir }) {
  const [pacientes, setPacientes] = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(false);
  const [busqueda, setBusqueda]   = useState('');
  const [pageSize, setPageSize]   = useState(10);
  const [pagina, setPagina]       = useState(1);
  const [sortCol, setSortCol]     = useState('APELLIDO_PATERNO');
  const [sortDir, setSortDir]     = useState('ASC');
  const [verRapido, setVerRapido] = useState(null); // COD del paciente con vista rápida abierta
  const [resumenRapido, setResumenRapido] = useState(null);
  const [cargandoResumen, setCargandoResumen] = useState(false);
  const [kpis, setKpis] = useState(null);
  const busqRef = useRef('');
  useEffect(() => {
    api.get('/pacientes/kpis').then(({ data }) => setKpis(data)).catch((e) => console.error(e));
  }, []);

  const cargar = useCallback(async (q, pg, ps, sc, sd) => {
    setLoading(true);
    try {
      const offset = (pg - 1) * ps;
      const { data } = await api.get(
        `/pacientes?q=${encodeURIComponent(q)}&limit=${ps}&offset=${offset}&sort=${sc}&dir=${sd}`
      );
      setPacientes(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const contarTotal = useCallback(async (q) => {
    try {
      const { data } = await api.get(`/pacientes/contar?q=${encodeURIComponent(q)}`);
      setTotal(data.total);
    } catch { setTotal(0); }
  }, []);

  useEffect(() => {
    busqRef.current = busqueda;
    const t = setTimeout(() => {
      if (busqRef.current !== busqueda) return;
      setPagina(1);
      contarTotal(busqueda);
      cargar(busqueda, 1, pageSize, sortCol, sortDir);
    }, 350);
    return () => clearTimeout(t);
  }, [busqueda, pageSize, sortCol, sortDir]);

  useEffect(() => {
    cargar(busqueda, pagina, pageSize, sortCol, sortDir);
  }, [pagina]);

  const totalPags = Math.ceil(total / pageSize) || 1;

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'ASC' ? 'DESC' : 'ASC');
    else { setSortCol(col); setSortDir('ASC'); }
    setPagina(1);
  };

  const abrirVistaRapida = async (p) => {
    if (verRapido === p.COD) { setVerRapido(null); setResumenRapido(null); return; }
    setVerRapido(p.COD); setResumenRapido(null); setCargandoResumen(true);
    try {
      const { data } = await api.get(`/pacientes/${p.COD}/resumen`);
      setResumenRapido(data);
    } catch (e) { console.error(e); }
    setCargandoResumen(false);
  };
  const thCls = (col) => sortCol === col ? `sort-${sortDir.toLowerCase()}` : '';

  const buildPages = () => {
    const pages = [];
    if (totalPags <= 7) {
      for (let i = 1; i <= totalPags; i++) pages.push(i);
    } else {
      pages.push(1);
      if (pagina > 3) pages.push('...');
      for (let i = Math.max(2, pagina-1); i <= Math.min(totalPags-1, pagina+1); i++) pages.push(i);
      if (pagina < totalPags - 2) pages.push('...');
      pages.push(totalPags);
    }
    return pages;
  };

  const desde = total === 0 ? 0 : (pagina-1)*pageSize + 1;
  const hasta  = Math.min(pagina*pageSize, total);

  return (
    <>
    <div className="pac-kpis">
      <div style={{background:'var(--surface)',borderRadius:10,border:'1px solid var(--border)',padding:'14px 16px'}}>
        <div style={{fontSize:10.5,textTransform:'uppercase',fontWeight:700,color:'var(--text3)',letterSpacing:.4}}>Pacientes activos</div>
        <div style={{fontSize:24,fontWeight:800,color:'var(--navy)',margin:'4px 0 2px'}}>{kpis ? kpis.activos.toLocaleString() : '—'}</div>
        <div style={{fontSize:10.5,color:'#2e7d4f',fontWeight:700}}>total registrado</div>
      </div>
      <div style={{background:'var(--surface)',borderRadius:10,border:'1px solid var(--border)',padding:'14px 16px'}}>
        <div style={{fontSize:10.5,textTransform:'uppercase',fontWeight:700,color:'var(--text3)',letterSpacing:.4}}>Nuevos este mes</div>
        <div style={{fontSize:24,fontWeight:800,color:'var(--navy)',margin:'4px 0 2px'}}>{kpis ? kpis.nuevosMes.toLocaleString() : '—'}</div>
        <div style={{fontSize:10.5,color:'var(--navy)',fontWeight:700}}>{new Date().toLocaleDateString('es-EC',{month:'long',year:'numeric'})}</div>
      </div>
      <div style={{background:'var(--surface)',borderRadius:10,border:'1px solid var(--border)',padding:'14px 16px'}}>
        <div style={{fontSize:10.5,textTransform:'uppercase',fontWeight:700,color:'var(--text3)',letterSpacing:.4}}>Con alergias registradas</div>
        <div style={{fontSize:24,fontWeight:800,color:'var(--navy)',margin:'4px 0 2px'}}>{kpis ? kpis.conAlergias.toLocaleString() : '—'}</div>
        <div style={{fontSize:10.5,color:'var(--red)',fontWeight:700}}>visibles en la vista rápida</div>
      </div>
      <div style={{background:'var(--surface)',borderRadius:10,border:'1px solid var(--border)',padding:'14px 16px'}}>
        <div style={{fontSize:10.5,textTransform:'uppercase',fontWeight:700,color:'var(--text3)',letterSpacing:.4}}>Cumpleaños esta semana</div>
        <div style={{fontSize:24,fontWeight:800,color:'var(--navy)',margin:'4px 0 2px'}}>{kpis ? kpis.cumpleSemana.toLocaleString() : '—'}</div>
        <div style={{fontSize:10.5,color:'var(--text3)',fontWeight:700}}>detalle opcional</div>
      </div>
    </div>
    <div className="pac-layout">
    <div className="fade-up" style={{background:'var(--surface)',borderRadius:10,border:'1px solid var(--border)',overflow:'hidden',flex:1,minWidth:0}}>
      <div className="pac-header">
        <span className="pac-header-title">Pacientes</span>
        <button className="btn btn-sm" style={{background:'var(--navy)',color:'#fff',border:'none',fontWeight:700}}
          onClick={onNuevo}>+ Nuevo</button>
      </div>

      <div className="pac-toolbar">
        <label style={{fontSize:12,color:'var(--text2)',display:'flex',alignItems:'center',gap:6}}>
          Mostrar
          <select className="form-control" style={{width:75,padding:'5px 8px'}}
            value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPagina(1); }}>
            {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <div className="search-wrap" style={{flex:1,maxWidth:420,position:'relative'}}>
          <span className="search-icon" style={{fontSize:13}}>⊙</span>
          <input className="form-control" placeholder="Buscar por nombre, apellido o cédula..."
            value={busqueda} onChange={e => setBusqueda(e.target.value)} />
          {busqueda && (
            <span onClick={() => setBusqueda('')}
              style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',cursor:'pointer',color:'var(--text3)',fontSize:14}}>✕</span>
          )}
        </div>
      </div>

      <div className="table-wrap pac-table-desktop">
        <table className="pac-table" style={{width:'100%',borderCollapse:'collapse'}}>
          <thead>
            <tr>
              <th className={thCls('IDENTIFICACION')} onClick={() => handleSort('IDENTIFICACION')} style={{width:130}}>Cédula</th>
              <th className={thCls('APELLIDO_PATERNO')} onClick={() => handleSort('APELLIDO_PATERNO')}>Paciente</th>
              <th className={`hide-mobile ${thCls('FECHA_NACIMIENTO')}`} onClick={() => handleSort('FECHA_NACIMIENTO')} style={{width:110}}>Edad / Sexo</th>
              <th className="hide-mobile" style={{width:130}}>Teléfono</th>
              <th style={{width:120,textAlign:'center'}}>Acción</th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? <tr><td colSpan={5} style={{textAlign:'center',padding:'32px 0'}}><div className="spinner" style={{margin:'0 auto'}} /></td></tr>
              : pacientes.length === 0
                ? <tr><td colSpan={5}><div className="empty-state">Sin resultados</div></td></tr>
                : pacientes.map(p => (
                  <tr key={p.COD}>
                    <td style={{fontWeight:600,color:'var(--navy)',fontFamily:'monospace',fontSize:12}}>
                      {p.IDENTIFICACION
                        ? p.IDENTIFICACION
                        : <span title="Paciente sin cédula registrada — riesgo de quedar duplicado si se le agrega después"
                            style={{color:'var(--red)',fontWeight:700,fontFamily:'inherit',fontSize:10.5}}>⚠ Sin cédula</span>}
                    </td>
                    <td>
                      <div style={{display:'flex',alignItems:'center',gap:9}}>
                        <div style={{
                          width:30,height:30,borderRadius:'50%',flexShrink:0,
                          background: p.C_SEXO==='F' ? '#fbeaf5' : 'var(--blue-lt)',
                          color: p.C_SEXO==='F' ? '#993556' : 'var(--navy)',
                          display:'flex',alignItems:'center',justifyContent:'center',
                          fontSize:10,fontWeight:700
                        }}>
                          {p.PRIMER_NOMBRE?.[0]}{p.APELLIDO_PATERNO?.[0]}
                        </div>
                        <div>
                          <div style={{fontWeight:500}}>{p.NOMBRE_COMPLETO?.trim()}</div>
                          <div style={{fontSize:10.5,color:'var(--text3)'}}>{p.C_SEXO==='F'?'registrada':'registrado'} {fmtFecha(p.FECHA_REGISTRO)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="hide-mobile" style={{color:'var(--text2)',fontSize:12.5}}>{calcEdad(p.FECHA_NACIMIENTO).replace(' años','')} · {p.C_SEXO}</td>
                    <td className="hide-mobile" style={{color:'var(--text2)',fontSize:12}}>{p.TELEFONO||'—'}</td>
                    <td style={{textAlign:'center'}}>
                      <div style={{display:'flex',gap:4,justifyContent:'center'}}>
                        <div className="act-icon" title="Vista rápida" onClick={() => abrirVistaRapida(p)}
                          style={{background: verRapido===p.COD ? 'var(--navy)' : undefined, color: verRapido===p.COD ? '#fff' : undefined}}>👁</div>
                        <div className="act-icon" title="Editar" onClick={() => onEditar(p)}>✎</div>
                        <div className="act-icon" title="Historia clínica" onClick={() => onHistoria(p)}>⏱</div>
                        <div className="act-icon" title="Imprimir HCU-001" onClick={() => onImprimir(p)} style={{fontSize:13}}>⎙</div>
                      </div>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {/* ── Vista celular: tarjetas en vez de tabla (evita el scroll
           horizontal que corta el boton "+ Nuevo" y el icono de Imprimir) ── */}
      <div className="pac-cards-mobile">
        {loading ? (
          <div className="spinner" style={{margin:'20px auto'}} />
        ) : pacientes.length === 0 ? (
          <div className="empty-state">Sin resultados</div>
        ) : pacientes.map(p => (
          <div key={p.COD} className="pat-card">
            <div className="pat-top">
              <div className="pat-av" style={{
                background: p.C_SEXO==='F' ? '#fbeaf5' : 'var(--blue-lt)',
                color: p.C_SEXO==='F' ? '#993556' : 'var(--navy)',
              }}>
                {p.PRIMER_NOMBRE?.[0]}{p.APELLIDO_PATERNO?.[0]}
              </div>
              <div className="pat-info">
                <div className="pat-name">{p.NOMBRE_COMPLETO?.trim()}</div>
                <div className="pat-meta">
                  {p.IDENTIFICACION
                    ? <span>🪪 {p.IDENTIFICACION}</span>
                    : <span style={{color:'var(--red)',fontWeight:700}}>⚠ Sin cédula</span>}
                  <span>· {p.C_SEXO==='F'?'registrada':'registrado'} {fmtFecha(p.FECHA_REGISTRO)}</span>
                </div>
              </div>
            </div>
            <div className="pat-acts">
              <button className={verRapido===p.COD ? 'on' : ''} title="Vista rápida" onClick={() => abrirVistaRapida(p)}>👁</button>
              <button title="Editar" onClick={() => onEditar(p)}>✎</button>
              <button title="Historia clínica" onClick={() => onHistoria(p)}>⏱</button>
              <button title="Imprimir HCU-001" onClick={() => onImprimir(p)}>⎙</button>
            </div>
          </div>
        ))}
      </div>

      <div className="pac-footer">
        <span className="pac-footer-info">
          {total > 0 ? `Mostrando ${desde} a ${hasta} de ${total.toLocaleString()} registros` : 'Sin registros'}
        </span>
        <div className="pag">
          <button className="pag-btn" disabled={pagina===1} onClick={() => setPagina(1)} title="Primera">⏮</button>
          <button className="pag-btn" disabled={pagina===1} onClick={() => setPagina(p=>p-1)} title="Anterior">‹</button>
          {buildPages().map((p,i) =>
            p === '...'
              ? <span key={`e${i}`} className="pag-ellipsis">…</span>
              : <button key={p} className={`pag-btn ${pagina===p?'active':''}`} onClick={() => setPagina(p)}>{p}</button>
          )}
          <button className="pag-btn" disabled={pagina===totalPags} onClick={() => setPagina(p=>p+1)} title="Siguiente">›</button>
          <button className="pag-btn" disabled={pagina===totalPags} onClick={() => setPagina(totalPags)} title="Última">⏭</button>
        </div>
      </div>
    </div>
        {verRapido && (
          <div className="pac-side-panel">
            <div style={{background:'var(--navy)',color:'#fff',padding:'16px 16px',display:'flex',alignItems:'flex-start',justifyContent:'space-between'}}>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                {resumenRapido && (
                  <div style={{
                    width:38,height:38,borderRadius:'50%',flexShrink:0,
                    background: resumenRapido.SEXO==='Femenino' ? '#fbeaf5' : 'var(--blue-lt)',
                    color: resumenRapido.SEXO==='Femenino' ? '#993556' : 'var(--navy)',
                    display:'flex',alignItems:'center',justifyContent:'center',
                    fontSize:13,fontWeight:700
                  }}>
                    {iniciales(resumenRapido.NOMBRE_COMPLETO)}
                  </div>
                )}
                <div>
                  <div style={{fontSize:14,fontWeight:700}}>{resumenRapido?.NOMBRE_COMPLETO || 'Cargando...'}</div>
                  {resumenRapido && <div style={{fontSize:11,color:'#c9d4e2',marginTop:2}}>HC {resumenRapido.HC} · {resumenRapido.EDAD} años · {resumenRapido.SEXO}</div>}
                </div>
              </div>
              <span onClick={() => { setVerRapido(null); setResumenRapido(null); }}
                style={{cursor:'pointer',fontSize:14,opacity:.8}}>✕</span>
            </div>
            <div style={{padding:'14px 16px'}}>
              {cargandoResumen ? (
                <div style={{textAlign:'center',padding:'24px 0',color:'var(--text3)',fontSize:12.5}}>Cargando...</div>
              ) : !resumenRapido ? (
                <div style={{textAlign:'center',padding:'24px 0',color:'var(--text3)',fontSize:12.5}}>No se pudo cargar</div>
              ) : (
                <>
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:10,textTransform:'uppercase',fontWeight:700,color:'var(--text3)',marginBottom:6}}>Alergias</div>
                  {resumenRapido.alergias.length === 0
                    ? <span style={{fontSize:12,color:'var(--text3)'}}>Sin alergias registradas</span>
                    : resumenRapido.alergias.map((a,i) => (
                      <span key={i} style={{display:'inline-block',background:'#fdeaea',color:'var(--red)',fontSize:10.5,fontWeight:700,padding:'3px 9px',borderRadius:999,margin:'0 5px 5px 0'}}>⚠ {a}</span>
                    ))}
                </div>
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:10,textTransform:'uppercase',fontWeight:700,color:'var(--text3)',marginBottom:6}}>Últimos signos vitales</div>
                  {resumenRapido.vitales ? (
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                      <div style={{background:'#f8f9fb',borderRadius:8,padding:'8px 10px'}}>
                        <div style={{fontSize:9.5,color:'var(--text3)',fontWeight:700}}>PRESIÓN</div>
                        <div style={{fontSize:14,fontWeight:800,color:'var(--navy)'}}>{resumenRapido.vitales.PA || '—'}</div>
                      </div>
                      <div style={{background:'#f8f9fb',borderRadius:8,padding:'8px 10px'}}>
                        <div style={{fontSize:9.5,color:'var(--text3)',fontWeight:700}}>PULSO</div>
                        <div style={{fontSize:14,fontWeight:800,color:'var(--navy)'}}>{resumenRapido.vitales.PULSO || '—'}</div>
                      </div>
                      <div style={{background:'#f8f9fb',borderRadius:8,padding:'8px 10px'}}>
                        <div style={{fontSize:9.5,color:'var(--text3)',fontWeight:700}}>TEMP.</div>
                        <div style={{fontSize:14,fontWeight:800,color:'var(--navy)'}}>{resumenRapido.vitales.TEMP || '—'}</div>
                      </div>
                      <div style={{background:'#f8f9fb',borderRadius:8,padding:'8px 10px'}}>
                        <div style={{fontSize:9.5,color:'var(--text3)',fontWeight:700}}>SPO2</div>
                        <div style={{fontSize:14,fontWeight:800,color:'var(--navy)'}}>{resumenRapido.vitales.SAT_O2 || '—'}</div>
                      </div>
                    </div>
                  ) : <span style={{fontSize:12,color:'var(--text3)'}}>Sin registro reciente</span>}
                </div>
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:10,textTransform:'uppercase',fontWeight:700,color:'var(--text3)',marginBottom:6}}>Historia reciente</div>
                  {resumenRapido.diagnosticosRecientes.length === 0
                    ? <span style={{fontSize:12,color:'var(--text3)'}}>Sin registros</span>
                    : resumenRapido.diagnosticosRecientes.map((d,i) => (
                      <div key={i} style={{borderLeft:'2px solid var(--border)',paddingLeft:10,marginBottom:8}}>
                        <div style={{fontSize:10,color:'var(--text3)'}}>{fmtFecha(d.FECHA)}</div>
                        <div style={{fontSize:12,color:'var(--navy)',fontWeight:600}}>{d.DESCRIPCION || d.CIE10}</div>
                      </div>
                    ))}
                </div>
                <div>
                  <div style={{fontSize:10,textTransform:'uppercase',fontWeight:700,color:'var(--text3)',marginBottom:6}}>Medicación activa</div>
                  {resumenRapido.medicacionActiva.length === 0
                    ? <span style={{fontSize:12,color:'var(--text3)'}}>Sin receta activa</span>
                    : resumenRapido.medicacionActiva.map((m,i) => (
                      <div key={i} style={{background:'#e6f4ea',color:'#2e7d4f',fontSize:11.5,fontWeight:700,padding:'7px 10px',borderRadius:8,marginBottom:6}}>💊 {m}</div>
                    ))}
                </div>
                </>
              )}
            </div>
          </div>
        )}
        </div>
    </>
  );
}

/* -- Campo reutilizable — FUERA de FormPaciente para que React
      no lo destruya/recree en cada render (bug de pérdida de foco) -- */
function CampoForm({ k, label, req, type='text', opts, placeholder, form, errors, set }) {
  return (
    <div>
      <label className="form-label">{label}{req && <span style={{color:'var(--red)'}}> *</span>}</label>
      {opts
        ? <select className="form-control" style={errors[k]?{borderColor:'var(--red)'}:{}}
            value={form[k]||''} onChange={e => set(k, e.target.value)}>
            <option value="">Seleccione</option>
            {opts.map(o => <option key={o.v||o.COD} value={o.v||o.COD}>{o.l||o.DESCRIPCION}</option>)}
          </select>
        : <input className="form-control" type={type} placeholder={placeholder}
            style={errors[k]?{borderColor:'var(--red)'}:{}}
            value={form[k]||''} onChange={e => set(k, e.target.value)} />}
      {errors[k] && <div style={{fontSize:10,color:'var(--red)',marginTop:2}}>{errors[k]}</div>}
    </div>
  );
}

function FormPaciente({ paciente, onVolver, onGuardado, onAbrirDuplicado }) {
  const esNuevo = !paciente?.COD;
  const [tabActiva, setTabActiva] = useState(0);

  /* -- Estado: datos de filiación -- */
  const [form, setForm] = useState({
    C_TIPO_IDENTIFICACION: 'CDL',
    IDENTIFICACION: '',
    APELLIDO_PATERNO: '', APELLIDO_MATERNO: '',
    PRIMER_NOMBRE: '',    SEGUNDO_NOMBRE: '',
    FECHA_NACIMIENTO: '', C_SEXO: '',
    C_ESTADO_CIVIL: '',   C_INSTRUCCION: '',  C_OCUPACION: '',
    NACIONALIDAD: '',     AUTOIDENTIFICACION: '', TIPO_BONO: '',
    COD_PROVINCIA: '',    COD_CANTON: '',     COD_PARROQUIA: '',
    DIRECCION_DOMICILIO: '',
    CALLE_PRINCIPAL: '',  CALLE_SECUNDARIA: '', NUMERACION: '', REFERENCIA: '', BARRIO: '',
    TELEFONO: '',         EMAIL: '',
    EMPRESA_TRABAJO: '',  DIRECCION_TRABAJO: '', TELEFONO_TRABAJO: '',
    ...paciente,
  });

  /* -- Estado: contactos de emergencia (personas_referentes) -- */
  const CONTACTO_VACIO = { NOMBRES:'', TELEFONO:'', EMAIL:'', DIRECCION:'', RELACION:'Cónyuge' };
  const [contactos, setContactos] = useState([{ ...CONTACTO_VACIO }]);

  /* -- Estado: facturación (clientes) -- */
  const FAC_VACIO = { C_TIPO_IDENTIFICACION:'CDL', IDENTIFICACION:'', APELLIDOS:'', NOMBRES:'', DIRECCION:'', TELEFONO:'', EMAIL:'' };
  const [facturacion, setFacturacion] = useState([{ ...FAC_VACIO }]);

  /* -- Catálogos -- */
  const [provincias,  setProvincias]  = useState([]);
  const [cantones,    setCantones]    = useState([]);
  const [parroquias,  setParroquias]  = useState([]);
  const [instruccion, setInstruccion] = useState([]);
  const [ocupacion,   setOcupacion]   = useState([]);
  const [estadoCivil, setEstadoCivil] = useState([]);

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [msg,    setMsg]    = useState('');
  // Paciente existente detectado con la misma cédula al intentar guardar
  // (evita crear un duplicado por error al editar/registrar)
  const [dupCedula, setDupCedula] = useState(null);
  // FOTO_URL en BD: /uploads/fotos/paciente_123.jpg
  // Se sirve desde la raiz de la API (sin /api), ej: http://localhost:3001/uploads/fotos/...
  const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(/\/api$/, '');
  const [fotoPreview, setFotoPreview] = useState(paciente?.FOTO_URL ? `${API_BASE}${paciente.FOTO_URL}` : null);
  const [fotoFile,    setFotoFile]    = useState(null);
  const fotoRef = useRef(null);

  /* -- Autocompletar por cédula -- */
  const [buscandoCedula, setBuscandoCedula] = useState(false);
  const [msgCedula, setMsgCedula] = useState('');
  const buscarPorCedula = async () => {
    const cedula = (form.IDENTIFICACION || '').trim();
    if (!/^\d{10}$/.test(cedula)) { setMsgCedula('Ingresa una cédula válida de 10 dígitos'); return; }
    setBuscandoCedula(true); setMsgCedula('');
    try {
      const { data } = await api.get(`/pacientes/consultar-cedula/${cedula}`);
      setForm(f => ({
        ...f,
        APELLIDO_PATERNO: data.APELLIDO_PATERNO || f.APELLIDO_PATERNO,
        APELLIDO_MATERNO: data.APELLIDO_MATERNO || f.APELLIDO_MATERNO,
        PRIMER_NOMBRE:    data.PRIMER_NOMBRE    || f.PRIMER_NOMBRE,
        SEGUNDO_NOMBRE:   data.SEGUNDO_NOMBRE   || f.SEGUNDO_NOMBRE,
        FECHA_NACIMIENTO: data.FECHA_NACIMIENTO || f.FECHA_NACIMIENTO,
        C_SEXO:           data.C_SEXO           || f.C_SEXO,
      }));
      setMsgCedula('✓ Datos completados');
    } catch (e) {
      setMsgCedula(e.response?.data?.error || 'No se pudo consultar la cédula');
    }
    setBuscandoCedula(false);
  };
  /* -- Autocompletar por cédula (Datos de facturación) -- */
  const [buscandoCedulaFac, setBuscandoCedulaFac] = useState(null);
  const [msgCedulaFac, setMsgCedulaFac] = useState({});
  const buscarPorCedulaFac = async (i) => {
    const cedula = (facturacion[i]?.IDENTIFICACION || '').trim();
    if (!/^\d{10}$/.test(cedula)) { setMsgCedulaFac(m => ({...m,[i]:'Ingresa una cédula válida de 10 dígitos'})); return; }
    setBuscandoCedulaFac(i); setMsgCedulaFac(m => ({...m,[i]:''}));
    try {
      const { data } = await api.get(`/pacientes/consultar-cedula/${cedula}`);
      setFac(i, 'APELLIDOS', `${data.APELLIDO_PATERNO||''} ${data.APELLIDO_MATERNO||''}`.trim());
      setFac(i, 'NOMBRES', `${data.PRIMER_NOMBRE||''} ${data.SEGUNDO_NOMBRE||''}`.trim());
      setMsgCedulaFac(m => ({...m,[i]:'✓ Datos completados'}));
    } catch (e) {
      setMsgCedulaFac(m => ({...m,[i]: e.response?.data?.error || 'No se pudo consultar la cédula'}));
    }
    setBuscandoCedulaFac(null);
  };

  /* -- Carga inicial -- */
  useEffect(() => {
    api.get('/catalogos/provincias').then(r => setProvincias(r.data)).catch(()=>{});
    api.get('/catalogos/instruccion').then(r => setInstruccion(r.data)).catch(()=>{});
    api.get('/catalogos/ocupacion').then(r => setOcupacion(r.data)).catch(()=>{});
    api.get('/catalogos/estado-civil').then(r => setEstadoCivil(r.data)).catch(()=>{});

    if (!esNuevo && paciente?.COD) {
      // Cargar contactos y facturación existentes
      api.get(`/pacientes/${paciente.COD}/contactos`)
        .then(r => { if (r.data.length) setContactos(r.data); })
        .catch(()=>{});
      api.get(`/pacientes/${paciente.COD}/facturacion`)
        .then(r => { if (r.data) setFacturacion([r.data]); })
        .catch(()=>{});
      // Cargar cadena provincia->cantón para la parroquia guardada
      if (paciente.COD_PROVINCIA) {
        api.get(`/catalogos/cantones/${paciente.COD_PROVINCIA}`).then(r => setCantones(r.data)).catch(()=>{});
      }
      if (paciente.COD_CANTON) {
        api.get(`/catalogos/parroquias/${paciente.COD_CANTON}`).then(r => setParroquias(r.data)).catch(()=>{});
      }
    }
  }, []);

  /* -- Helpers filiación -- */
  const set = (k, v) => { setForm(f => ({...f, [k]:v})); setErrors(e => ({...e, [k]:''})); };

  const onProvincia = async (cod) => {
    set('COD_PROVINCIA', cod); set('COD_CANTON',''); set('COD_PARROQUIA','');
    setCantones([]); setParroquias([]);
    if (cod) { const { data } = await api.get(`/catalogos/cantones/${cod}`); setCantones(data); }
  };
  const onCanton = async (cod) => {
    set('COD_CANTON', cod); set('COD_PARROQUIA','');
    setParroquias([]);
    if (cod) { const { data } = await api.get(`/catalogos/parroquias/${cod}`); setParroquias(data); }
  };

  /* -- Helpers contactos -- */
  const setContacto = (i, k, v) => setContactos(cs => cs.map((c,idx) => idx===i ? {...c,[k]:v} : c));
  const addContacto    = () => setContactos(cs => [...cs, {...CONTACTO_VACIO}]);
  const removeContacto = (i) => setContactos(cs => cs.filter((_,idx) => idx!==i));

  /* -- Helpers facturación -- */
  const setFac = (i, k, v) => setFacturacion(fs => fs.map((f,idx) => idx===i ? {...f,[k]:v} : f));
  const addFac    = () => setFacturacion(fs => [...fs, {...FAC_VACIO}]);
  const removeFac = (i) => setFacturacion(fs => fs.filter((_,idx) => idx!==i));

  /* -- Validación -- */
  const validar = () => {
    const e = {};
    if (!form.APELLIDO_PATERNO) e.APELLIDO_PATERNO = 'Requerido';
    if (!form.PRIMER_NOMBRE)    e.PRIMER_NOMBRE    = 'Requerido';
    if (!form.C_SEXO)           e.C_SEXO           = 'Requerido';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* -- Guardar todo --
     forzar=true omite el chequeo de cédula duplicada (lo usa el botón
     "Guardar de todas formas" del aviso de duplicado) -- */
  const guardar = async (forzar = false) => {
    if (!validar()) { setTabActiva(0); return; }
    setSaving(true); setMsg('');
    if (!forzar) setDupCedula(null);
    try {
      // 0. Chequeo de cédula duplicada — evita crear un paciente repetido
      //    por error al editar/registrar (se puede omitir con "forzar")
      const cedulaForm = (form.IDENTIFICACION || '').trim();
      if (!forzar && cedulaForm) {
        try {
          const excluirQ = !esNuevo && paciente?.COD ? `?excluir=${paciente.COD}` : '';
          const { data: chk } = await api.get(`/pacientes/verificar-cedula/${encodeURIComponent(cedulaForm)}${excluirQ}`);
          if (chk.existe) {
            setDupCedula(chk.paciente);
            setSaving(false);
            return;
          }
        } catch (e) {
          // Si la verificación falla (ej. red), no bloqueamos el guardado
        }
      }

      // COD_PROVINCIA y COD_CANTON ahora SÍ son columnas propias de
      // pacientes (requiere migracion-provincia-canton.sql aplicada) —
      // ya no se excluyen, viajan junto con el resto de form.
      const payload = form;

      // 1. Paciente principal
      let codPaciente = paciente?.COD;
      if (esNuevo) {
        const { data } = await api.post('/pacientes', payload);
        codPaciente = data.COD;
      } else {
        await api.put(`/pacientes/${codPaciente}`, payload);
      }

      // 2. Contactos de emergencia — solo los que tienen nombre
      const contactosValidos = contactos.filter(c => c.NOMBRES?.trim());
      await api.post(`/pacientes/${codPaciente}/contactos`, contactosValidos);

      // 3. Facturación — solo el primero si tiene identificación
      const facValida = facturacion[0];
      if (facValida?.IDENTIFICACION?.trim() && facValida?.APELLIDOS?.trim() && facValida?.NOMBRES?.trim()) {
        await api.post(`/pacientes/${codPaciente}/facturacion`, facValida);
      }

      // 4. Foto — si el usuario seleccionó una nueva imagen
      if (fotoFile) {
        const formData = new FormData();
        formData.append('foto', fotoFile);
        await api.post(`/pacientes/${codPaciente}/foto`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      setMsg('✓ Guardado correctamente');
      setTimeout(() => onGuardado(), 1000);
    } catch (e) {
      setMsg('Error: ' + (e.response?.data?.error || e.message));
    } finally { setSaving(false); }
  };

  /* -- Alias corto para no reescribir todo el JSX -- */
  // F ya está definido fuera del componente — pasamos las props de contexto via bind
  // Fc eliminado — usar CampoForm directamente con props

  const TABS = [
    { label:'Datos de filiación',     icon:'◉' },
    { label:'Contacto de emergencia', icon:'☎' },
    { label:'Datos de facturación',   icon:'◈' },
  ];

  /* -- Estilo tabla grilla -- */
  const thStyle = { padding:'9px 10px', fontSize:11, fontWeight:600, color:'#fff',
                    background:'#1a2535', borderRight:'1px solid #2e3d52', whiteSpace:'nowrap' };
  const tdStyle = { padding:'5px 6px', borderBottom:'1px solid var(--border)', verticalAlign:'middle' };
  const inpStyle = { width:'100%', padding:'5px 8px', fontSize:12, border:'1px solid var(--border)',
                     borderRadius:5, background:'#fff', color:'var(--text)' };
  const selStyle = { ...inpStyle };
  const btnAdd = { width:26, height:26, borderRadius:5, border:'none', background:'#0d6efd',
                   color:'#fff', fontSize:16, cursor:'pointer', display:'flex',
                   alignItems:'center', justifyContent:'center', flexShrink:0 };
  const btnDel = { width:26, height:26, borderRadius:5, border:'none', background:'#dc3545',
                   color:'#fff', fontSize:14, cursor:'pointer', display:'flex',
                   alignItems:'center', justifyContent:'center', flexShrink:0 };

  const RELACIONES = ['Cónyuge','Padre','Madre','Hijo/a','Hermano/a','Abuelo/a','Tío/a','Amigo/a','Otro'];
  const TIPOS_ID   = [{v:'CDL',l:'Cédula'},{v:'PSP',l:'Pasaporte'},{v:'RUC',l:'RUC'},{v:'NAP',l:'No aplica'}];

  return (
    <div className="fade-up" style={{background:'var(--surface)',borderRadius:10,border:'1px solid var(--border)'}}>

      {/* Encabezado */}
      <div style={{padding:'12px 20px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <button className="btn btn-sm" onClick={onVolver}>← Atrás</button>
          <span style={{fontSize:14,fontWeight:700,color:'var(--text)'}}>
            {esNuevo ? 'Nuevo paciente' : 'Editar paciente'}
          </span>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          {msg && <span style={{fontSize:12,color:msg.startsWith('✓')?'var(--green)':'var(--red)',fontWeight:500}}>{msg}</span>}
          <button className="btn btn-primary btn-sm" disabled={saving} onClick={() => guardar()}>
            {saving ? 'Guardando...' : '✓ Guardar'}
          </button>
        </div>
      </div>

      {/* Aviso: ya existe un paciente con esta cédula */}
      {dupCedula && (
        <div style={{
          margin:'14px 20px 0', padding:'12px 14px', background:'#fff8e6',
          border:'1px solid #f0c36d', borderRadius:8, display:'flex',
          alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10,
        }}>
          <div style={{fontSize:12.5,color:'#7a5b00'}}>
            ⚠ Ya existe un paciente registrado con esta cédula: <strong>{dupCedula.NOMBRE_COMPLETO}</strong>
            {dupCedula.TELEFONO ? ` · ${dupCedula.TELEFONO}` : ''}
          </div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            <button type="button" className="btn btn-sm" onClick={() => onAbrirDuplicado?.(dupCedula)}>
              Abrir ese paciente
            </button>
            <button type="button" className="btn btn-sm" style={{color:'var(--red)',borderColor:'var(--red)'}}
              disabled={saving} onClick={() => guardar(true)}>
              Guardar de todas formas
            </button>
            <button type="button" className="btn btn-sm" onClick={() => setDupCedula(null)}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="reg-tabs">
        {TABS.map((t,i) => (
          <div key={i} className={`reg-tab ${tabActiva===i?'active':''}`} onClick={() => setTabActiva(i)}>
            <span style={{fontSize:13}}>{t.icon}</span> {t.label}
          </div>
        ))}
      </div>

      {/* == TAB 0: Datos de filiación == */}
      {tabActiva === 0 && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px 20px',padding:20}}>

          {/* Zona de foto — ocupa columna derecha fila 1 a 4 */}
          <div style={{gridColumn:2,gridRow:'1/5',display:'flex',flexDirection:'column',alignItems:'center',gap:10,paddingTop:4}}>
            <div
              onClick={() => fotoRef.current?.click()}
              style={{
                width:130,height:130,borderRadius:'50%',border:'2px dashed var(--border)',
                display:'flex',alignItems:'center',justifyContent:'center',
                cursor:'pointer',overflow:'hidden',background:'var(--surface2)',
                transition:'border-color .15s',flexShrink:0,
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor='var(--navy)'}
              onMouseLeave={e => e.currentTarget.style.borderColor='var(--border)'}
            >
              {fotoPreview
                ? <img src={fotoPreview} alt="Foto paciente"
                    style={{width:'100%',height:'100%',objectFit:'cover'}} />
                : <div style={{textAlign:'center',padding:10}}>
                    <div style={{fontSize:44,opacity:.2}}>👤</div>
                    <div style={{fontSize:10,color:'var(--text3)',marginTop:4}}>Subir foto</div>
                  </div>
              }
            </div>
            <input ref={fotoRef} type="file" accept="image/jpeg,image/png,image/webp"
              style={{display:'none'}}
              onChange={e => {
                const file = e.target.files[0];
                if (!file) return;
                if (file.size > 3 * 1024 * 1024) { alert('La imagen no puede superar 3 MB'); return; }
                setFotoFile(file);
                setFotoPreview(URL.createObjectURL(file));
              }} />
            {fotoPreview && (
              <button className="btn btn-sm"
                style={{color:'var(--red)',borderColor:'var(--red)',background:'transparent',fontSize:11}}
                onClick={async () => {
                  setFotoPreview(null); setFotoFile(null);
                  if (!esNuevo && paciente?.COD) {
                    try { await api.delete(`/pacientes/${paciente.COD}/foto`); } catch {}
                  }
                }}>
                Eliminar imagen
              </button>
            )}

          </div>

          <div className="section-label" style={{gridColumn:1}}>Identificación</div>
          <CampoForm form={form} errors={errors} set={set} k="C_TIPO_IDENTIFICACION" label="Tipo identificación" opts={TIPOS_ID} />
          <div>
            <CampoForm form={form} errors={errors} set={set} k="IDENTIFICACION" label="Número de identificación" placeholder="Ej: 1712345678" />
            {form.C_TIPO_IDENTIFICACION === 'CDL' && (
              <>
                <button type="button" className="btn btn-sm" style={{marginTop:4}}
                  disabled={buscandoCedula || !/^\d{10}$/.test(form.IDENTIFICACION||'')}
                  onClick={buscarPorCedula}>
                  {buscandoCedula ? 'Buscando...' : '🔍 Autocompletar con cédula'}
                </button>
                {msgCedula && <div style={{fontSize:11,marginTop:3,color:msgCedula.startsWith('✓')?'var(--green)':'var(--red)'}}>{msgCedula}</div>}
              </>
            )}
          </div>

          <div className="section-label">Nombres y apellidos</div>
          <div>
            <label className="form-label">Apellidos <span style={{color:'var(--red)'}}>*</span></label>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
              <div>
                <input className="form-control" placeholder="Paterno"
                  style={errors.APELLIDO_PATERNO?{borderColor:'var(--red)'}:{}}
                  value={form.APELLIDO_PATERNO||''} onChange={e => set('APELLIDO_PATERNO',e.target.value)} />
                {errors.APELLIDO_PATERNO && <div style={{fontSize:10,color:'var(--red)',marginTop:2}}>{errors.APELLIDO_PATERNO}</div>}
              </div>
              <input className="form-control" placeholder="Materno"
                value={form.APELLIDO_MATERNO||''} onChange={e => set('APELLIDO_MATERNO',e.target.value)} />
            </div>
          </div>
          <div>
            <label className="form-label">Nombres <span style={{color:'var(--red)'}}>*</span></label>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
              <div>
                <input className="form-control" placeholder="Primer nombre"
                  style={errors.PRIMER_NOMBRE?{borderColor:'var(--red)'}:{}}
                  value={form.PRIMER_NOMBRE||''} onChange={e => set('PRIMER_NOMBRE',e.target.value)} />
                {errors.PRIMER_NOMBRE && <div style={{fontSize:10,color:'var(--red)',marginTop:2}}>{errors.PRIMER_NOMBRE}</div>}
              </div>
              <input className="form-control" placeholder="Segundo nombre"
                value={form.SEGUNDO_NOMBRE||''} onChange={e => set('SEGUNDO_NOMBRE',e.target.value)} />
            </div>
          </div>

          <div className="section-label">Datos demográficos</div>
          <div>
            <label className="form-label">Fecha de nacimiento</label>
            <input type="date" className="form-control"
              value={form.FECHA_NACIMIENTO ? form.FECHA_NACIMIENTO.substring(0,10) : ''}
              onChange={e => set('FECHA_NACIMIENTO',e.target.value)} />
            {form.FECHA_NACIMIENTO && (
              <div style={{fontSize:11,color:'var(--text3)',marginTop:3}}>{calcEdad(form.FECHA_NACIMIENTO)}</div>
            )}
          </div>
          <CampoForm form={form} errors={errors} set={set} k="C_SEXO" label="Sexo" req opts={[{v:'M',l:'Masculino'},{v:'F',l:'Femenino'}]} />
          <CampoForm form={form} errors={errors} set={set} k="NACIONALIDAD" label="Nacionalidad" opts={[
            {v:'ECU',l:'Ecuatoriana'},{v:'COL',l:'Colombiana'},{v:'PER',l:'Peruana'},
            {v:'VEN',l:'Venezolana'},{v:'USA',l:'Estadounidense'},{v:'OTR',l:'Otra'},
          ]} />
          <CampoForm form={form} errors={errors} set={set} k="AUTOIDENTIFICACION" label="Autoidentificacion etnica" opts={[
            {v:'MES',l:'Mestizo/a'},{v:'IND',l:'Indigena'},{v:'AFR',l:'Afroecuatoriano/a'},
            {v:'MON',l:'Montubio/a'},{v:'BLA',l:'Blanco/a'},{v:'OTR',l:'Otro'},
          ]} />
          <CampoForm form={form} errors={errors} set={set} k="C_ESTADO_CIVIL" label="Estado civil" opts={estadoCivil.length ? estadoCivil : [
            {v:'SLT',l:'Soltero/a'},{v:'CSD',l:'Casado/a'},{v:'DVR',l:'Divorciado/a'},
            {v:'VDO',l:'Viudo/a'},{v:'UNL',l:'Unión libre'},
          ]} />
          <CampoForm form={form} errors={errors} set={set} k="C_INSTRUCCION" label="Escolaridad" opts={instruccion.length ? instruccion : [
            {v:'INF',l:'Infante'},{v:'NNG',l:'Ninguna'},
            {v:'PRI',l:'Primaria incompleta'},{v:'PRC',l:'Primaria completa'},
            {v:'SCI',l:'Secundaria incompleta'},{v:'SCC',l:'Secundaria completa'},
            {v:'SPI',l:'Superior incompleta'},{v:'SPC',l:'Superior completa'},
          ]} />
          <CampoForm form={form} errors={errors} set={set} k="C_OCUPACION" label="Ocupacion" opts={ocupacion.length ? ocupacion : []} />
          <CampoForm form={form} errors={errors} set={set} k="TIPO_BONO" label="Tipo de bono" opts={[
            {v:'BDH',l:'Bono de Desarrollo Humano'},{v:'PEN',l:'Pension asistencial'},
            {v:'JUB',l:'Jubilacion'},{v:'NIN',l:'Ninguno'},
          ]} />

          <div className="section-label">Contacto y ubicación</div>
          <div>
            <label className="form-label">Teléfonos</label>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
              <input className="form-control" placeholder="Principal"
                value={form.TELEFONO||''} onChange={e => set('TELEFONO',e.target.value)} />
              <input className="form-control" placeholder="Trabajo"
                value={form.TELEFONO_TRABAJO||''} onChange={e => set('TELEFONO_TRABAJO',e.target.value)} />
            </div>
          </div>
          <CampoForm form={form} errors={errors} set={set} k="EMAIL" label="Correo electrónico" type="email" />

          <div>
            <label className="form-label">Provincia</label>
            <select className="form-control" value={form.COD_PROVINCIA||''} onChange={e => onProvincia(e.target.value)}>
              <option value="">Seleccione</option>
              {provincias.map(p => <option key={p.COD} value={p.COD}>{p.PROVINCIA}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Cantón</label>
            <select className="form-control" value={form.COD_CANTON||''} onChange={e => onCanton(e.target.value)} disabled={!cantones.length}>
              <option value="">Seleccione</option>
              {cantones.map(c => <option key={c.COD} value={c.COD}>{c.CANTON}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Parroquia</label>
            <select className="form-control" value={form.COD_PARROQUIA||''} onChange={e => set('COD_PARROQUIA',e.target.value)} disabled={!parroquias.length}>
              <option value="">Seleccione</option>
              {parroquias.map(p => <option key={p.COD} value={p.COD}>{p.PARROQUIA}</option>)}
            </select>
          </div>
          <div style={{gridColumn:'1/-1'}}>
            <label className="form-label">Direccion domicilio</label>
            <input className="form-control" placeholder="Descripcion general"
              value={form.DIRECCION_DOMICILIO||''} onChange={e => set('DIRECCION_DOMICILIO',e.target.value)} />
          </div>
          <div>
            <label className="form-label">Calle principal</label>
            <input className="form-control" value={form.CALLE_PRINCIPAL||''} onChange={e => set('CALLE_PRINCIPAL',e.target.value)} />
          </div>
          <div>
            <label className="form-label">Numeracion</label>
            <input className="form-control" value={form.NUMERACION||''} onChange={e => set('NUMERACION',e.target.value)} />
          </div>
          <div>
            <label className="form-label">Calle secundaria</label>
            <input className="form-control" value={form.CALLE_SECUNDARIA||''} onChange={e => set('CALLE_SECUNDARIA',e.target.value)} />
          </div>
          <div>
            <label className="form-label">Referencia</label>
            <input className="form-control" value={form.REFERENCIA||''} onChange={e => set('REFERENCIA',e.target.value)} />
          </div>
          <div>
            <label className="form-label">Barrio / Sector</label>
            <input className="form-control" value={form.BARRIO||''} onChange={e => set('BARRIO',e.target.value)} />
          </div>
          <CampoForm form={form} errors={errors} set={set} k="EMPRESA_TRABAJO"  label="Empresa donde trabaja" />
          <div style={{gridColumn:'1/-1'}}>
            <label className="form-label">Dirección del trabajo</label>
            <input className="form-control"
              value={form.DIRECCION_TRABAJO||''} onChange={e => set('DIRECCION_TRABAJO',e.target.value)} />
          </div>

          {!esNuevo && paciente?.PARROQUIA && (
            <div style={{gridColumn:'1/-1',background:'var(--blue-lt)',borderRadius:8,padding:'8px 12px',fontSize:12,color:'var(--navy)'}}>
              Ubicación actual: <strong>{paciente.PARROQUIA}</strong>
              {paciente.CANTON && <> · {paciente.CANTON}</>}
              {paciente.PROVINCIA && <> · {paciente.PROVINCIA}</>}
            </div>
          )}
        </div>
      )}

      {/* == TAB 1: Contacto de emergencia == */}
      {tabActiva === 1 && (
        <div style={{padding:20}}>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead>
                <tr>
                  <th style={{...thStyle,width:32}}>
                    <button style={btnAdd} title="Agregar contacto" onClick={addContacto}>+</button>
                  </th>
                  <th style={thStyle}>Nombre Completo <span style={{color:'#f87'}}>*</span></th>
                  <th style={thStyle}>Teléfono</th>
                  <th style={thStyle}>Parentesco</th>
                  <th style={thStyle}>Dirección</th>
                  <th style={thStyle}>Correo Electrónico</th>
                </tr>
              </thead>
              <tbody>
                {contactos.map((c,i) => (
                  <tr key={i}>
                    <td style={{...tdStyle,textAlign:'center'}}>
                      <button style={btnDel} onClick={() => removeContacto(i)}>✕</button>
                    </td>
                    <td style={tdStyle}>
                      <input style={inpStyle} value={c.NOMBRES||''} placeholder="Nombre completo"
                        onChange={e => setContacto(i,'NOMBRES',e.target.value)} />
                    </td>
                    <td style={tdStyle}>
                      <input style={inpStyle} value={c.TELEFONO||''} placeholder="Teléfono"
                        onChange={e => setContacto(i,'TELEFONO',e.target.value)} />
                    </td>
                    <td style={tdStyle}>
                      <select style={selStyle} value={c.RELACION||''} onChange={e => setContacto(i,'RELACION',e.target.value)}>
                        <option value="">Seleccione</option>
                        {RELACIONES.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </td>
                    <td style={tdStyle}>
                      <input style={inpStyle} value={c.DIRECCION||''} placeholder="Dirección"
                        onChange={e => setContacto(i,'DIRECCION',e.target.value)} />
                    </td>
                    <td style={tdStyle}>
                      <input style={inpStyle} type="email" value={c.EMAIL||''} placeholder="Email"
                        onChange={e => setContacto(i,'EMAIL',e.target.value)} />
                    </td>
                  </tr>
                ))}
                {contactos.length === 0 && (
                  <tr><td colSpan={6} style={{textAlign:'center',padding:'24px 0',color:'var(--text3)',fontSize:12}}>
                    Sin contactos — presione + para agregar
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* == TAB 2: Datos de facturación == */}
      {tabActiva === 2 && (
        <div style={{padding:20}}>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead>
                <tr>
                  <th style={{...thStyle,width:32}}>
                    <button style={btnAdd} title="Agregar receptor" onClick={addFac}>+</button>
                  </th>
                  <th style={{...thStyle,width:50}}>Copiar</th>
                  <th style={thStyle}>Tipo identificación <span style={{color:'#f87'}}>*</span></th>
                  <th style={thStyle}>Razón social — Cédula/RUC <span style={{color:'#f87'}}>*</span></th>
                  <th style={thStyle}>Teléfono <span style={{color:'#f87'}}>*</span></th>
                  <th style={thStyle}>Dirección <span style={{color:'#f87'}}>*</span></th>
                  <th style={thStyle}>Correo electrónico <span style={{color:'#f87'}}>*</span></th>
                </tr>
              </thead>
              <tbody>
                {facturacion.map((f,i) => (
                  <tr key={i}>
                    <td style={{...tdStyle,textAlign:'center'}}>
                      <button style={btnDel} onClick={() => removeFac(i)}>✕</button>
                    </td>
                    <td style={{...tdStyle,textAlign:'center'}}>
                      {/* Copiar datos del paciente */}
                      <button title="Copiar datos del paciente"
                        style={{...btnAdd,background:'var(--navy)',fontSize:11,width:50,borderRadius:5}}
                        onClick={() => setFac(i, 'IDENTIFICACION', form.IDENTIFICACION || '') ||
                          setFac(i,'C_TIPO_IDENTIFICACION', form.C_TIPO_IDENTIFICACION) ||
                          setFac(i,'APELLIDOS', ((form.APELLIDO_PATERNO||'')+' '+(form.APELLIDO_MATERNO||'')).trim()) ||
                          setFac(i,'NOMBRES', ((form.PRIMER_NOMBRE||'')+' '+(form.SEGUNDO_NOMBRE||'')).trim()) ||
                          setFac(i,'TELEFONO', form.TELEFONO||'') ||
                          setFac(i,'EMAIL', form.EMAIL||'') ||
                          setFac(i,'DIRECCION', form.DIRECCION_DOMICILIO||'')
                        }>
                        Copiar
                      </button>
                    </td>
                    <td style={tdStyle}>
                      <select style={selStyle} value={f.C_TIPO_IDENTIFICACION||''} onChange={e => setFac(i,'C_TIPO_IDENTIFICACION',e.target.value)}>
                        <option value="">Seleccione</option>
                        {TIPOS_ID.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
                      </select>
                    </td>
                    <td style={tdStyle}>
                      <input style={inpStyle} value={f.IDENTIFICACION||''} placeholder="Identificación"
                        onChange={e => setFac(i,'IDENTIFICACION',e.target.value)} />
                      {f.C_TIPO_IDENTIFICACION === 'CDL' && (
                        <>
                          <button type="button" style={{marginTop:3,fontSize:10,padding:'2px 6px',cursor:'pointer'}}
                            className="btn btn-sm"
                            disabled={buscandoCedulaFac===i || !/^\d{10}$/.test(f.IDENTIFICACION||'')}
                            onClick={() => buscarPorCedulaFac(i)}>
                            {buscandoCedulaFac===i ? 'Buscando...' : '🔍 Autocompletar'}
                          </button>
                          {msgCedulaFac[i] && <div style={{fontSize:10,marginTop:2,color:msgCedulaFac[i].startsWith('✓')?'var(--green)':'var(--red)'}}>{msgCedulaFac[i]}</div>}
                        </>
                      )}
                      <input style={{...inpStyle,marginTop:3}} value={f.APELLIDOS||''} placeholder="Apellidos"
                        onChange={e => setFac(i,'APELLIDOS',e.target.value)} />
                      <input style={{...inpStyle,marginTop:3}} value={f.NOMBRES||''} placeholder="Nombres / Razón social"
                        onChange={e => setFac(i,'NOMBRES',e.target.value)} />
                    </td>
                    <td style={tdStyle}>
                      <input style={inpStyle} value={f.TELEFONO||''} placeholder="Teléfono"
                        onChange={e => setFac(i,'TELEFONO',e.target.value)} />
                    </td>
                    <td style={tdStyle}>
                      <input style={inpStyle} value={f.DIRECCION||''} placeholder="Dirección"
                        onChange={e => setFac(i,'DIRECCION',e.target.value)} />
                    </td>
                    <td style={tdStyle}>
                      <input style={inpStyle} type="email" value={f.EMAIL||''} placeholder="Email"
                        onChange={e => setFac(i,'EMAIL',e.target.value)} />
                    </td>
                  </tr>
                ))}
                {facturacion.length === 0 && (
                  <tr><td colSpan={7} style={{textAlign:'center',padding:'24px 0',color:'var(--text3)',fontSize:12}}>
                    Sin datos de facturación — presione + para agregar
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
}

/* ===========================================
   PÁGINA PRINCIPAL
=========================================== */
export default function Pacientes() {
  const navigate = useNavigate();
  const [modo, setModo]               = useState('lista');
  const [pacSelected, setPacSelected] = useState(null);

  const abrirEdicion = async (p) => {
    try {
      // Cargamos la ficha completa para tener COD_PROVINCIA y COD_CANTON del JOIN
      const { data } = await api.get(`/pacientes/${p.COD}`);
      setPacSelected(data);
    } catch {
      setPacSelected(p);
    }
    setModo('form');
  };

  return (
    <>
      <style>{css}</style>
      <div className="fade-up">
        {modo === 'lista' && (
          <ListaPacientes
            onNuevo={() => { setPacSelected(null); setModo('form'); }}
            onEditar={abrirEdicion}
            onHistoria={p => navigate(`/historia?paciente=${p.COD}`)}
            onImprimir={p => {
              const url = `${(process.env.REACT_APP_API_URL||'http://localhost:3001/api')}/reportes/hcu/${p.COD}`;
              const token = localStorage.getItem('token');
              fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                .then(r => r.blob())
                .then(blob => {
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(blob);
                  a.download = `HCU_${p.APELLIDO_PATERNO}_${p.IDENTIFICACION}.pdf`;
                  a.click();
                })
                .catch(e => alert('Error generando PDF: ' + e.message));
            }}
          />
        )}
        {modo === 'form' && (
          <FormPaciente
            paciente={pacSelected}
            onVolver={() => setModo('lista')}
            onGuardado={() => setModo('lista')}
            onAbrirDuplicado={(dup) => abrirEdicion({ COD: dup.COD })}
          />
        )}
      </div>
    </>
  );
}
